/**
 * A hash router, hand-rolled because react-router is not a dependency.
 *
 * Hash rather than history so the SPA can be served from any path without a
 * server rewrite rule, and so the URL shape matches the system being replaced
 * (#/1/page/pur_ords/6726).
 */
import { useCallback, useSyncExternalStore } from 'react'

export type Query = Readonly<Record<string, string>>

/**
 * Every route inside the application carries the company it is in. Two routes
 * are not inside it, so they are their own types — and the type system, rather
 * than a convention, is what keeps the shell off them: the supplier portal,
 * which has no account at all, and the platform console, whose account is
 * outside every tenant.
 */
export type Route =
  | SignedInRoute
  | { readonly name: 'quote'; readonly token: string }
  | { readonly name: 'platform'; readonly section: string }

export type SignedInRoute =
  | { readonly name: 'home'; readonly companyId: string }
  | { readonly name: 'hub'; readonly companyId: string; readonly hub: string }
  | { readonly name: 'settings'; readonly companyId: string }
  | { readonly name: 'admin'; readonly companyId: string; readonly section: string }
  | { readonly name: 'password'; readonly companyId: string }
  | { readonly name: 'list'; readonly companyId: string; readonly docRoute: string; readonly query: Query }
  | { readonly name: 'create'; readonly companyId: string; readonly docRoute: string; readonly query: Query }
  | { readonly name: 'document'; readonly companyId: string; readonly docRoute: string; readonly id: string; readonly query: Query }
  | { readonly name: 'masterList'; readonly companyId: string; readonly masterRoute: string; readonly query: Query }
  | { readonly name: 'masterRecord'; readonly companyId: string; readonly masterRoute: string; readonly id: string }
  | { readonly name: 'report'; readonly companyId: string; readonly reportRoute: string; readonly query: Query }
  /** Project Drive is a page, not a report, whatever the incumbent's URL says. */
  | { readonly name: 'drive'; readonly companyId: string; readonly query: Query }
  | { readonly name: 'dashboard'; readonly companyId: string; readonly dashboardRoute: string; readonly query: Query }
  | { readonly name: 'unknown'; readonly companyId: string; readonly path: string }

export const DEFAULT_COMPANY = '1'

const parseQuery = (raw: string | undefined): Query => {
  const out: Record<string, string> = {}
  if (!raw) return out
  for (const [k, v] of new URLSearchParams(raw)) out[k] = v
  return out
}

export const parsePath = (hash: string): Route => {
  const raw = hash.replace(/^#/, '')
  const [path = '', search] = raw.split('?')
  const segs = path.split('/').filter((s) => s.length > 0)
  const query = parseQuery(search)
  const companyId = segs[0] ?? DEFAULT_COMPANY

  // #/quote/<token> is the supplier portal: no company, and no session.
  if (segs[0] === 'quote' && segs[1]) return { name: 'quote', token: segs[1] }

  // #/platform is the console for the operator of the whole installation. It
  // is matched before the company segment because it has no company: every
  // other route's first segment is a company id.
  if (segs[0] === 'platform') return { name: 'platform', section: segs[1] ?? '' }

  if (segs.length <= 1) return { name: 'home', companyId }
  if (segs[1] === 'home') {
    const hub = segs[2]
    return hub ? { name: 'hub', companyId, hub } : { name: 'home', companyId }
  }
  // docs/01 and docs/14 both name #/<company>/notifications; the Notification
  // Centre lives under the home hub. An old link resolves rather than
  // answering `unknown`.
  if (segs[1] === 'notifications') return { name: 'hub', companyId, hub: 'alerts' }
  // docs/11 addresses settings at #/1/settings, outside the hub namespace.
  if (segs[1] === 'settings') return { name: 'settings', companyId }
  if (segs[1] === 'admin') return { name: 'admin', companyId, section: segs[2] ?? '' }
  if (segs[1] === 'password') return { name: 'password', companyId }
  if (segs[1] === 'page') {
    const docRoute = segs[2]
    if (!docRoute) return { name: 'unknown', companyId, path }
    const tail = segs[3]
    if (!tail) return { name: 'list', companyId, docRoute, query }
    if (tail === 'create') return { name: 'create', companyId, docRoute, query }
    return { name: 'document', companyId, docRoute, id: tail, query }
  }
  if (segs[1] === 'master') {
    const masterRoute = segs[2]
    if (!masterRoute) return { name: 'unknown', companyId, path }
    const tail = segs[3]
    return tail
      ? { name: 'masterRecord', companyId, masterRoute, id: tail }
      : { name: 'masterList', companyId, masterRoute, query }
  }
  if (segs[1] === 'drive') return { name: 'drive', companyId, query }
  if (segs[1] === 'reports') {
    const reportRoute = segs[2]
    // The system being replaced files Project Drive under /reports/ even though
    // it is a file store. Old links keep working; new ones go to /drive.
    if (reportRoute === 'project_drive') return { name: 'drive', companyId, query }
    return reportRoute ? { name: 'report', companyId, reportRoute, query } : { name: 'hub', companyId, hub: 'reports' }
  }
  if (segs[1] === 'dashboard') {
    const dashboardRoute = segs[2]
    return dashboardRoute ? { name: 'dashboard', companyId, dashboardRoute, query } : { name: 'hub', companyId, hub: 'dashboards' }
  }
  return { name: 'unknown', companyId, path }
}

const withQuery = (base: string, query?: Query): string => {
  if (!query) return base
  const params = new URLSearchParams(query)
  const s = params.toString()
  return s ? `${base}?${s}` : base
}

export const paths = {
  platform: (section?: string) => (section ? `#/platform/${section}` : '#/platform'),
  home: (companyId: string) => `#/${companyId}/home`,
  hub: (companyId: string, hub: string) => `#/${companyId}/home/${hub}`,
  settings: (companyId: string) => `#/${companyId}/settings`,
  admin: (companyId: string, section: string) => (section ? `#/${companyId}/admin/${section}` : `#/${companyId}/admin`),
  password: (companyId: string) => `#/${companyId}/password`,
  list: (companyId: string, docRoute: string, query?: Query) => withQuery(`#/${companyId}/page/${docRoute}`, query),
  create: (companyId: string, docRoute: string, query?: Query) => withQuery(`#/${companyId}/page/${docRoute}/create`, query),
  masterList: (companyId: string, masterRoute: string, query?: Query) => withQuery(`#/${companyId}/master/${masterRoute}`, query),
  masterRecord: (companyId: string, masterRoute: string, id: string) => `#/${companyId}/master/${masterRoute}/${id}`,
  report: (companyId: string, reportRoute: string, query?: Query) => withQuery(`#/${companyId}/reports/${reportRoute}`, query),
  drive: (companyId: string, query?: Query) => withQuery(`#/${companyId}/drive`, query),
  dashboard: (companyId: string, dashboardRoute: string, query?: Query) => withQuery(`#/${companyId}/dashboard/${dashboardRoute}`, query),
  document: (companyId: string, docRoute: string, id: string) => `#/${companyId}/page/${docRoute}/${id}`,
}

/* ---- subscription ------------------------------------------------------ */

const listeners = new Set<() => void>()
const announce = () => { for (const l of listeners) l() }

let currentHash = typeof window === 'undefined' ? '' : window.location.hash
let currentRoute = parsePath(currentHash)

const refresh = () => {
  const next = typeof window === 'undefined' ? '' : window.location.hash
  if (next === currentHash) return
  currentHash = next
  currentRoute = parsePath(next)
  announce()
}

if (typeof window !== 'undefined') window.addEventListener('hashchange', refresh)

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export const useRoute = (): Route => useSyncExternalStore(subscribe, () => currentRoute, () => currentRoute)

/* ---- the unsaved-changes guard ----------------------------------------- */

let dirtyProbe: (() => boolean) | null = null
let pendingTarget: string | null = null
const pendingListeners = new Set<() => void>()
const announcePending = () => { for (const l of pendingListeners) l() }

/** A page with an editable form registers here; only one may be live at a time. */
export const registerDirtyProbe = (probe: () => boolean): (() => void) => {
  dirtyProbe = probe
  return () => { if (dirtyProbe === probe) dirtyProbe = null }
}

export const navigate = (target: string, opts: { force?: boolean } = {}): void => {
  if (!opts.force && dirtyProbe?.()) {
    pendingTarget = target
    announcePending()
    return
  }
  pendingTarget = null
  window.location.hash = target.startsWith('#') ? target : `#${target}`
  // Assigning an identical hash fires no event, yet a repeated click should
  // still settle the page back onto that route.
  refresh()
}

export const usePendingNavigation = () => {
  const target = useSyncExternalStore(
    (l) => { pendingListeners.add(l); return () => { pendingListeners.delete(l) } },
    () => pendingTarget,
    () => pendingTarget,
  )
  const discard = useCallback(() => {
    const t = pendingTarget
    pendingTarget = null
    dirtyProbe = null
    announcePending()
    if (t) navigate(t, { force: true })
  }, [])
  const stay = useCallback(() => {
    pendingTarget = null
    announcePending()
  }, [])
  return { target, discard, stay }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (!dirtyProbe?.()) return
    e.preventDefault()
    e.returnValue = ''
  })
}
